import { subscribe } from '@grubhubprod/umami-library/dist/utilities/eventManager';
import React, { useEffect, useRef, useState } from 'react';
import { Snackbar } from 'src/components/snackbar/Snackbar';
import { getWindow } from 'src/utilities/window';

export const SNACKBAR_ROOT_ID = 'snackbar-root';

export enum SnackbarStatus {
  IN_PROGRESS = 'in_progress',
  SUCCESS = 'success',
  ERROR = 'error',
}
export interface ISnackbarConfig {
  id: string;
  status: SnackbarStatus;
  message: string;
}

export interface ISnackbarLauncherState {
  snackbars: ISnackbarConfig[];
}

export const DISMISSED_NOTIFICATION_LS_KEY = 'dismissed_snackbar_notifications';

export function SnackbarLauncher(): JSX.Element {
  const [snackbars, setSnackbars] = useState<ISnackbarConfig[]>([]);
  const snackbarsRef = useRef(null);
  snackbarsRef.current = snackbars;
  const window = getWindow();

  /**
   * Get dismissed notifications from Local Storage
   * @param taskId
   */
  function getDismissedNotification(): string[] {
    const value = window.localStorage.getItem(DISMISSED_NOTIFICATION_LS_KEY);
    return value ? JSON.parse(value) || [] : [];
  }

  /**
   * Removes task from state
   * @param taskId
   */
  function onDismiss(taskId: string): void {
    const list = [...snackbarsRef.current];
    const index = list.findIndex((sbc) => sbc.id === taskId);
    if (index !== -1) {
      const currentConfig = list[index];
      if (currentConfig) {
        dismissNotification(currentConfig.id);
        list.splice(index, 1);
        setSnackbars(list);
        snackbarsRef.current = list;
      }
    }
  }

  /**
   * Store dismissed notifications into Local Storage
   * This mechanism ensures to not show already dismissed in-progress notification
   * while still getting in-progress status from API
   *
   * @param taskId
   */
  function dismissNotification(taskId: string): void {
    const dismissedFromLS = getDismissedNotification();
    // Do not store if exists
    if (dismissedFromLS.findIndex((id) => id === taskId) === -1) {
      dismissedFromLS.push(taskId);
      window.localStorage.setItem(
        DISMISSED_NOTIFICATION_LS_KEY,
        JSON.stringify(dismissedFromLS)
      );
    }
  }

  useEffect(() => {
    if (window) {
      window.localStorage.setItem(DISMISSED_NOTIFICATION_LS_KEY, '[]');
    }

    const subscription = subscribe('push:snackbar', (snackBarConfig) => {
      const snackbarList = [...snackbarsRef.current] as ISnackbarConfig[];
      const currentConfig = snackBarConfig as unknown as ISnackbarConfig;
      // If snackbar config does not have an id like the async tasks case, it provides a unique with Date.now
      currentConfig.id = currentConfig.id || Date.now().toString();
      const index = snackbarList.findIndex(
        (sbc) => sbc.id === currentConfig.id
      );

      if (index !== -1) {
        // update status of an existing snackbar
        snackbarList[index].message = currentConfig.message;
        snackbarList[index].status = currentConfig.status;
        setSnackbars(snackbarList);
      } else {
        // push new one unless dismissed before
        const dismissedFromLS = getDismissedNotification();
        if (
          !(
            currentConfig.status === SnackbarStatus.IN_PROGRESS &&
            dismissedFromLS.includes(currentConfig.id)
          )
        ) {
          snackbarList.push(currentConfig);
          setSnackbars(snackbarList);
        }
      }
    });

    return () => {
      subscription();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="snackbarContainer" data-testid="snackbar-container">
      {snackbars.map((sb) => (
        <Snackbar key={sb.id} config={sb} onDismiss={onDismiss} />
      ))}
    </div>
  );
}
